<script setup lang="ts">
import { computed, useSlots, ref, watch, nextTick, onMounted, onUnmounted } from "vue";

const props = withDefaults(
  defineProps<{
    value: boolean;
    size?: "sm" | "md" | "lg" | "xl" | "fit";
    customClass?: string;
    // Inert but still rendered: the caller keeps the control on screen and explains next to it why
    // there is nothing to switch to, instead of the switch disappearing. Announced with
    // `aria-disabled` rather than the native `disabled` attribute — Chrome fires no mouse events on
    // a natively disabled button, so a tooltip on it would never appear.
    disabled?: boolean;
  }>(),
  { size: undefined, customClass: undefined, disabled: false }
);

const emit = defineEmits<{
  (e: "update:value", value: boolean): void;
}>();

const slots = useSlots();
const hasRight = computed(() => !!slots.right);

const isLeft = computed(() => !props.value);

const sizingClass = computed(() => {
  if (!props.size) return "h-[30px] w-full";
  // For sm/md/lg/xl: fixed container widths. Each tab gets half (via flex-1).
  // These match the legacy LargeSwitch totals (sm 192, md 384, lg 576, xl 768).
  if (props.size === "xl") return "h-9 w-[48rem]";
  if (props.size === "lg") return "h-9 w-[36rem]";
  if (props.size === "md") return "h-9 w-96";
  if (props.size === "sm") return "h-9 w-48";
  if (props.size === "fit") return "h-9 w-fit";
  return "h-[30px] w-full";
});

// For fit mode each tab sizes to its own content + padding (different widths OK).
// For other sizes the container has a fixed width and tabs share it via `flex-1`.
const tabSizingClass = computed(() => (props.size === "fit" ? "flex-none" : "w-full flex-1"));

const btnPaddingClass = computed(() => (props.size === "fit" ? "px-6 py-2" : "px-3 py-2"));

const btnBaseClasses = computed(() =>
  [
    "relative h-full rounded-8 t-caption-bold whitespace-nowrap flex items-center justify-center",
    "transition-colors duration-fast ease-standard",
    "focus-ring interactive-active",
    tabSizingClass.value,
    btnPaddingClass.value,
    props.customClass,
  ]
    .filter(Boolean)
    .join(" ")
);

const activeTabClasses = "text-type-default";
const inactiveTabClasses = "text-type-muted hover:bg-app-blue-200/10 hover:text-type-default";

// While disabled the inactive tab drops its hover treatment — hovering a tab that cannot be chosen
// should not look like it can be.
const tabStateClasses = (isActive: boolean) => {
  if (isActive) return activeTabClasses;
  return props.disabled ? "text-type-muted" : inactiveTabClasses;
};

const containerRef = ref<HTMLDivElement | null>(null);
const leftTabRef = ref<HTMLButtonElement | null>(null);
const rightTabRef = ref<HTMLButtonElement | null>(null);

// Enable the slide transition only once the user actually changes the selection,
// so the pill never animates on initial mount or when the value is set during
// navigation — it just appears in the correct position.
const selectTab = (next: boolean) => {
  // `aria-disabled` doesn't stop the browser from firing the event, so the handlers refuse the
  // change themselves.
  if (props.disabled) return;
  ready.value = true;
  emit("update:value", next);
};

const onLeftKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return;
  if (event.key === "ArrowRight") {
    event.preventDefault();
    selectTab(true);
    rightTabRef.value?.focus();
  }
};

const onRightKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return;
  if (event.key === "ArrowLeft") {
    event.preventDefault();
    selectTab(false);
    leftTabRef.value?.focus();
  }
};

// The active highlight is a single absolutely-positioned pill that slides between
// tabs. We measure the active button's real geometry (offsetLeft/offsetWidth) so
// the slide works for both equal-width sizes and the content-width `fit` mode where
// the two tabs differ in width.
const indicator = ref({ left: 0, width: 0 });
// Gates the slide transition: stays false until the user changes the selection
// (see selectTab), so the pill never animates on mount or during page navigation —
// it just appears in the correct position.
const ready = ref(false);

const updateIndicator = () => {
  const activeTab = isLeft.value ? leftTabRef.value : rightTabRef.value;
  if (!activeTab) return;
  indicator.value = { left: activeTab.offsetLeft, width: activeTab.offsetWidth };
};

const indicatorStyle = computed(() => ({
  width: `${indicator.value.width}px`,
  transform: `translateX(${indicator.value.left}px)`,
}));

let resizeObserver: ResizeObserver | null = null;

watch(
  () => props.value,
  () => nextTick(updateIndicator)
);

onMounted(() => {
  nextTick(updateIndicator);
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(() => updateIndicator());
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<template>
  <div
    ref="containerRef"
    class="rounded-8 bg-slate-0 ring-slate-250 relative inline-flex h-fit items-stretch ring-1 select-none"
    :class="sizingClass"
    role="tablist"
    aria-label="Mode switch">
    <!-- Sliding active highlight — sits behind the labels and tracks the active tab -->
    <span
      aria-hidden="true"
      class="slide-indicator bg-app-blue-200/20 rounded-8 ring-app-blue-500 ease-standard pointer-events-none absolute top-0 bottom-0 left-0 ring-1"
      :class="ready ? 'duration-normal transition-[transform,width]' : ''"
      :style="indicatorStyle" />
    <!-- LEFT -->
    <button
      ref="leftTabRef"
      type="button"
      role="tab"
      :aria-selected="isLeft"
      :aria-disabled="disabled || undefined"
      :class="[btnBaseClasses, tabStateClasses(isLeft), { 'interactive-disabled': disabled }]"
      @click="selectTab(false)"
      @keydown="onLeftKeydown">
      <slot name="left"> </slot>
    </button>

    <!-- RIGHT -->
    <button
      v-if="hasRight"
      ref="rightTabRef"
      type="button"
      role="tab"
      :aria-selected="!isLeft"
      :aria-disabled="disabled || undefined"
      :class="[btnBaseClasses, tabStateClasses(!isLeft), { 'interactive-disabled': disabled }]"
      @click="selectTab(true)"
      @keydown="onRightKeydown">
      <slot name="right"></slot>
    </button>
  </div>
</template>

<style scoped>
@media (prefers-reduced-motion: reduce) {
  .transition-colors,
  .slide-indicator {
    transition: none !important;
  }
}
</style>
